import type { Transaction } from "@rocicorp/zero";
import {
  dropUsagesLostByGroupRemoval,
  dropConnectorApprovalsWithoutAccess,
} from "../connectorUsages";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { grantsForScope } from "./grantQueries";
import { applyGrantDiff, diffGrants } from "./validation/grantContent";
import type { GrantInput } from "./validation/grantShape";
import { assertGroupDeletable } from "./validation/groupDeletable";

type ReadTx = Pick<Transaction<Schema>, "run">;
type WriteTx = Transaction<Schema>;

type ExistingGrantRow = {
  id: string;
  userGroupId: string | null;
  userId: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

// A user is a member of a group if they hold ANY grant scoped to it, whatever
// the role — the same definition the UI (buildUserGroupMemberships) and
// cross-resource availability use. The membership helpers below therefore read
// and remove across all roles; only the add path picks a role, conferring the
// standard `user_group_member`.
export const userGrantsQuery = (groupId: string, tenantId: string) =>
  zql.roleGrants
    .where("scopeId", groupId)
    .where("tenantId", tenantId)
    .where("userId", "IS NOT", null);

export async function getGroupMemberIds(
  tx: ReadTx,
  groupId: string,
  tenantId: string
): Promise<string[]> {
  const grants = await tx.run(userGrantsQuery(groupId, tenantId));
  return [
    ...new Set(
      grants.map((g) => g.userId).filter((id): id is string => id != null)
    ),
  ];
}

// Deletes a group after checking it carries no external grants, clearing the
// membership/grants scoped to it first. Shared by the UI delete mutator and the
// SCIM delete endpoint so the two can't diverge on what cleanup a delete entails.
export async function deleteUserGroupCascade(
  tx: WriteTx,
  userGroupId: string,
  tenantId: string
): Promise<void> {
  const [group] = await tx.run(
    zql.userGroups.where("id", userGroupId).where("tenantId", tenantId)
  );
  if (!group) return;
  await assertGroupDeletable(tx, userGroupId);
  const memberIds = await getGroupMemberIds(tx, userGroupId, tenantId);
  await dropUsagesLostByGroupRemoval(
    tx,
    group.tenantId,
    userGroupId,
    memberIds
  );
  const scopedGrants = await tx.run(
    grantsForScope(userGroupId, group.tenantId)
  );
  for (const grant of scopedGrants) {
    await tx.mutate.roleGrants.delete({ id: grant.id });
  }
  await tx.mutate.userGroups.delete({ id: userGroupId });
}

// Callers may reconcile a subset of group members; other members’ grants are preserved.
export async function reconcileGroupUserGrants(
  tx: WriteTx,
  groups: readonly {
    groupId: string;
    existing: readonly ExistingGrantRow[];
    desired: readonly GrantInput[];
  }[],
  tenantId: string
): Promise<void> {
  const changes = groups.map(({ groupId, existing, desired }) => {
    const diff = diffGrants(existing, desired);
    const desiredUserIds = new Set(desired.map((grant) => grant.userId));
    const changedUserIds = new Set(
      [...diff.toInsert, ...diff.toDelete].flatMap((grant) =>
        grant.userId && desiredUserIds.has(grant.userId) ? [grant.userId] : []
      )
    );
    const removedUserIds = diff.removedUserIds.filter(
      (userId) => !desiredUserIds.has(userId)
    );
    return { groupId, diff, changedUserIds, removedUserIds };
  });

  // Cleanup must see the final memberships, including connector grants inherited through other groups in the batch.
  for (const { groupId, diff } of changes)
    await applyGrantDiff(tx, groupId, diff, tenantId);

  const usersBySpace = new Map<string, Set<string>>();
  for (const { groupId, changedUserIds } of changes) {
    if (changedUserIds.size > 0) {
      const spaces = await tx.run(
        zql.spaces
          .where("tenantId", tenantId)
          .whereExists("roleGrantsAsScope", (grants) =>
            grants.where("userGroupId", groupId)
          )
      );
      for (const space of spaces) {
        const users = usersBySpace.get(space.id) ?? new Set<string>();
        for (const userId of changedUserIds) users.add(userId);
        usersBySpace.set(space.id, users);
      }
    }
  }
  await dropConnectorApprovalsWithoutAccess(
    tx,
    tenantId,
    [...usersBySpace].map(([spaceId, userIds]) => ({
      spaceId,
      principals: [...userIds].map((userId) => ({ userId, userGroupId: null })),
    }))
  );
  for (const { groupId, removedUserIds } of changes) {
    await dropUsagesLostByGroupRemoval(tx, tenantId, groupId, removedUserIds);
  }
}

// Reconciles a group's membership to exactly `desiredUserIds`. Membership is
// role-agnostic: a user dropped from the set loses every grant they hold on the
// group, and a newly added user gets the standard `user_group_member` role. Used
// by the SCIM PUT/replace paths, so it’s authoritative.
export async function replaceGroupMembers(
  tx: WriteTx,
  groupId: string,
  desiredUserIds: Set<string>,
  tenantId: string
): Promise<void> {
  const existing = await tx.run(userGrantsQuery(groupId, tenantId));
  const currentIds = new Set(existing.map((grant) => grant.userId));
  await reconcileGroupUserGrants(
    tx,
    [
      {
        groupId,
        existing: existing.filter(
          (grant) => !desiredUserIds.has(grant.userId!)
        ),
        desired: [...desiredUserIds]
          .filter((id) => !currentIds.has(id))
          .map((userId) => ({
            userId,
            builtinRoleId: "user_group_member",
          })),
      },
    ],
    tenantId
  );
}

// Idempotently adds `userIds` as members, conferring the standard
// `user_group_member` role. A user who already holds any grant on the group is
// already a member and is skipped, so a re-add is a no-op rather than a
// `role_grants_unique` violation or a duplicate role.
export async function addGroupMembers(
  tx: WriteTx,
  groupId: string,
  userIds: readonly string[],
  tenantId: string
): Promise<void> {
  const existing = await tx.run(userGrantsQuery(groupId, tenantId));
  const existingUserIds = new Set(existing.map((g) => g.userId));
  const toAdd = userIds.filter((id) => !existingUserIds.has(id));
  await reconcileGroupUserGrants(
    tx,
    [
      {
        groupId,
        existing: [],
        desired: toAdd.map((userId) => ({
          userId,
          builtinRoleId: "user_group_member",
        })),
      },
    ],
    tenantId
  );
}
